/**
 * 2FA enforcement helper.
 *
 * Called in AppShell after session + policy load.
 * Returns true if the user's role is in require_2fa_roles AND they have no
 * verified MFA factor enrolled — meaning they must be redirected to enroll.
 */

export interface MfaFactor {
  id: string;
  type: string;
  status: string;
  created_at: string | null;
}

export interface TenantSecurityPolicy {
  tenant_id: string;
  require_2fa_roles: string[];
  session_timeout_min: number;
  max_sessions: number;
  updated_at: string;
}

/**
 * Returns true if 2FA is required for the given role and the user has no
 * verified factor enrolled.
 *
 * @param role      - The user's current role in the tenant (e.g. 'admin', 'member').
 * @param factors   - The list of enrolled MFA factors for the user.
 * @param policy    - The tenant's security policy row.
 */
export function check2faRequirement(
  role: string,
  factors: MfaFactor[],
  policy: TenantSecurityPolicy | null | undefined,
): boolean {
  if (!policy) return false;
  if (!policy.require_2fa_roles.includes(role)) return false;
  const hasVerifiedFactor = factors.some((f) => f.status === 'verified');
  return !hasVerifiedFactor;
}
